import { createReducer } from 'src/helpers/reducer';
import actionTypes from './actionTypes';
import { OPTION_LISTS } from './constants';
import type { OptionListKey } from './constants';
import { toOptionsRequestKey } from './helpers';
import type {
  IAsync,
  IFetchOptionsErrorPayload,
  IFetchOptionsRequestPayload,
  IFetchOptionsSuccessPayload,
  IListState,
  IOptionsRequestKey,
  IOverview,
  IPairDetail,
  IPositionAvailabilityState,
  IPositionDetail,
} from './types';

const initListState: IListState = {
  options: [],
  isLoading: false,
  hasError: false,
  hasLoaded: false,
  nextPage: null,
  search: '',
  scope: null,
  page: 1,
};

const idle = <T>(): IAsync<T> => ({ isLoading: false, hasError: false, data: null });

export const initState: IPositionAvailabilityState = {
  lists: {
    [OPTION_LISTS.UNIT]: initListState,
  },
  overview: idle<IOverview>(),
  position: idle<IPositionDetail>(),
  pair: idle<IPairDetail>(),
};

const withList = (
  state: IPositionAvailabilityState,
  list: OptionListKey,
  next: Partial<IListState>,
): IPositionAvailabilityState => ({ ...state, lists: { ...state.lists, [list]: { ...state.lists[list], ...next } } });

const isSuperseded = (payload: IOptionsRequestKey, current: IListState): boolean =>
  payload.search !== current.search || payload.page !== current.page;

const loading = <T>(current: IAsync<T>): IAsync<T> => ({ ...current, isLoading: true, hasError: false });
const loaded = <T>(data: T): IAsync<T> => ({ isLoading: false, hasError: false, data });
const failed = <T>(): IAsync<T> => ({ isLoading: false, hasError: true, data: null });

export default createReducer(initState, {
  [actionTypes.FETCH_OPTIONS_REQUEST]: (
    state: IPositionAvailabilityState,
    { payload }: { payload: IFetchOptionsRequestPayload },
  ) => {
    const { list, search, page } = toOptionsRequestKey(payload);

    return withList(state, list, { isLoading: true, hasError: false, search, page });
  },

  [actionTypes.FETCH_OPTIONS_SUCCESS]: (
    state: IPositionAvailabilityState,
    { payload }: { payload: IFetchOptionsSuccessPayload },
  ) => {
    if (isSuperseded(payload, state.lists[payload.list])) return state;

    return withList(state, payload.list, {
      isLoading: false,
      hasLoaded: true,
      nextPage: payload.nextPage,
      options: payload.append ? [...state.lists[payload.list].options, ...payload.options] : payload.options,
    });
  },

  [actionTypes.FETCH_OPTIONS_ERROR]: (
    state: IPositionAvailabilityState,
    { payload }: { payload: IFetchOptionsErrorPayload },
  ) => {
    if (isSuperseded(payload, state.lists[payload.list])) return state;

    return withList(state, payload.list, { isLoading: false, hasError: true });
  },

  [actionTypes.FETCH_OVERVIEW_REQUEST]: (state: IPositionAvailabilityState) => ({
    ...state,
    overview: loading(state.overview),
  }),
  [actionTypes.FETCH_OVERVIEW_SUCCESS]: (state: IPositionAvailabilityState, { payload }: { payload: IOverview }) => ({
    ...state,
    overview: loaded(payload),
  }),
  [actionTypes.FETCH_OVERVIEW_ERROR]: (state: IPositionAvailabilityState) => ({
    ...state,
    overview: failed<IOverview>(),
  }),

  [actionTypes.FETCH_POSITION_REQUEST]: (state: IPositionAvailabilityState) => ({
    ...state,
    position: loading(state.position),
  }),
  [actionTypes.FETCH_POSITION_SUCCESS]: (
    state: IPositionAvailabilityState,
    { payload }: { payload: IPositionDetail },
  ) => ({
    ...state,
    position: loaded(payload),
  }),
  [actionTypes.FETCH_POSITION_ERROR]: (state: IPositionAvailabilityState) => ({
    ...state,
    position: failed<IPositionDetail>(),
  }),

  [actionTypes.FETCH_PAIR_REQUEST]: (state: IPositionAvailabilityState) => ({
    ...state,
    pair: loading(state.pair),
  }),
  [actionTypes.FETCH_PAIR_SUCCESS]: (state: IPositionAvailabilityState, { payload }: { payload: IPairDetail }) => ({
    ...state,
    pair: loaded(payload),
  }),
  [actionTypes.FETCH_PAIR_ERROR]: (state: IPositionAvailabilityState) => ({ ...state, pair: failed<IPairDetail>() }),

  [actionTypes.CLEAR_STORE]: () => initState,
});
